import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { api } from '../lib/api'
import { useCommonCodes } from '../lib/codes'

/**
 * 게시판 목록 — 소식 · 이야기(개발 플랜 §6-1).
 *
 * **게시판 종류를 화면에 하드코딩하지 않는다.** `common_code`의 `board` 그룹에서 읽으므로
 * 발주기관이 종류를 늘려도 재배포가 필요 없다(A-3 회신에 따라 범위가 바뀔 수 있다).
 * 제목·설명도 그 라벨에서 만든다 — 015로 라벨이 바뀌었을 때 화면 문구만 옛 이름으로
 * 남는 일을 막는다.
 *
 * **선택한 게시판은 주소(`?board=`)에 남긴다.** 헤더의 `이야기`·`소식` 메뉴가 같은 화면을
 * 이 파라미터로 가르기 때문이다(App.tsx MENU). 로컬 state로만 두면 두 메뉴가 같은 곳에
 * 떨어지고, 링크 공유·새로고침·뒤로가기도 선택을 잃는다.
 */
export function BoardListPage() {
  const [params, setParams] = useSearchParams()
  const board = params.get('board') ?? ''
  const [q, setQ] = useState('')

  const boards = useCommonCodes('board')
  const posts = useQuery({
    queryKey: ['posts', { board, q }],
    queryFn: () => api.posts({ board, q, limit: 30 }),
  })

  // 알 수 없는 코드가 주소로 들어오면 못 찾는다. 서버는 그런 값에 빈 목록을 주므로
  // 화면이 깨지지는 않는다 — 제목만 전체와 같아진다.
  const current = boards.data?.find((b) => b.code === board)

  const setBoard = (next: string) => {
    // `replace`를 쓰지 않는다 — 탭 전환은 사용자의 이동이라 뒤로가기로 되돌아갈 수 있어야 한다.
    setParams(next === '' ? {} : { board: next })
  }

  return (
    <>
      {/* 로딩 중에는 아직 라벨을 모른다. 그때 '전체'라고 적으면 곧 바뀌므로 빈 제목 대신
          기본 이름을 쓴다 — 코드값 목록은 10분 캐시라 메뉴를 오갈 때는 이 상태가 안 보인다. */}
      <h1 className="page-title">{current?.label ?? '소식·이야기'}</h1>
      <p className="page-desc">
        {current === undefined
          ? '해봄 플랫폼이 올리는 글 전체입니다.'
          : `${current.label} 글 목록입니다.`}
      </p>

      <div className="admin-tabs">
        <button
          type="button"
          className={board === '' ? 'active' : undefined}
          onClick={() => setBoard('')}
        >
          전체
        </button>
        {boards.data?.map((b) => (
          <button
            key={b.code}
            type="button"
            className={board === b.code ? 'active' : undefined}
            onClick={() => setBoard(b.code)}
          >
            {b.label}
          </button>
        ))}
      </div>

      <div className="filters">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="제목·내용 검색"
          aria-label="소식 검색"
        />
      </div>

      <QueryState
        isPending={posts.isPending}
        error={posts.error}
        isEmpty={posts.data?.items.length === 0}
        emptyMessage="등록된 소식이 없습니다."
      >
        <p className="muted small" style={{ marginBottom: 12 }}>
          {posts.data?.total}건
        </p>
        <div className="post-list">
          {posts.data?.items.map((p) => (
            <Link key={p.id} className="post-row" to={`/board/${p.id}`}>
              <div className="post-row-head">
                <span className="tag">{p.board.label}</span>
                {p.org?.name !== null && p.org !== null && (
                  <span className="muted small">{p.org.name}</span>
                )}
                <span className="muted small post-date">
                  {p.publishedAt?.slice(0, 10) ?? ''}
                </span>
              </div>
              <h3>{p.title}</h3>
              <p className="muted small">{p.excerpt}</p>
            </Link>
          ))}
        </div>
      </QueryState>
    </>
  )
}
